/**
 * Cadastro de obras.
 *
 * O CAMPO QUE MAIS IMPORTA AQUI É O ENDEREÇO, e não é óbvio pela tela: ele é o
 * que o motorista vê no celular, com link do mapa. Obra sem endereço vira
 * ligação para perguntar onde é. Por isso a API devolve um aviso quando ele
 * fica vazio, e a tela mostra esse aviso em vez de engolir — salvar deu certo,
 * mas ficou faltando algo que só aparece lá na ponta.
 */

import { api, ErroApi } from '../../api.js';
import { escapar } from '../../comum/formato.js';
import { avisar, avisarBloqueante } from '../../comum/aviso.js';

interface ObraLista {
  id: number;
  codigo: string;
  nome: string;
  status: string | null;
  endereco: string | null;
  engenheiro: string | null;
  mestre: string | null;
  codigo_sienge: string | null;
  cliente: string | null;
  concluida_em: string | null;
}

/** Campos que a ficha edita, na ordem em que aparecem. */
const CAMPOS: { nome: string; rotulo: string; tipo?: string; largo?: boolean; dica?: string }[] = [
  { nome: 'codigo', rotulo: 'Código interno', dica: 'O que aparece nos romaneios, ex.: OVG-GRA' },
  { nome: 'nome', rotulo: 'Nome da obra', largo: true },
  { nome: 'status', rotulo: 'Situação' },
  { nome: 'codigoSienge', rotulo: 'Código no Sienge' },
  { nome: 'cliente', rotulo: 'Cliente', largo: true },
  { nome: 'endereco', rotulo: 'Endereço', largo: true, dica: 'O motorista vê isto com link do mapa — sempre preencher' },
  { nome: 'engenheiro', rotulo: 'Engenheiro' },
  { nome: 'mestre', rotulo: 'Mestre de obra' },
  { nome: 'responsavel', rotulo: 'Responsável' },
  { nome: 'tipoObra', rotulo: 'Tipo de obra' },
  { nome: 'tipoContrato', rotulo: 'Tipo de contrato' },
  { nome: 'prazo', rotulo: 'Prazo' },
  { nome: 'area', rotulo: 'Área (m²)', tipo: 'number' },
  { nome: 'dataInicio', rotulo: 'Início', tipo: 'date' },
  { nome: 'dataEntregaPrevista', rotulo: 'Entrega prevista', tipo: 'date' },
];

export function montarCadastroObras(raiz: HTMLElement): void {
  let situacao: 'ativas' | 'concluidas' = 'ativas';
  let ordem: 'nome' | 'codigo' = 'nome';

  raiz.innerHTML = `
    <div class="chips" id="abas-obra">
      <button class="chip" data-sit="ativas" aria-pressed="true">Em andamento</button>
      <button class="chip" data-sit="concluidas" aria-pressed="false">✓ Concluídas</button>
    </div>
    <div class="barra-busca">
      <input id="busca" placeholder="Procure pelo nome, código ou cliente…" autocomplete="off">
      <select id="ordem">
        <option value="nome">Ordem alfabética</option>
        <option value="codigo">Por código</option>
      </select>
      <button class="btn" id="nova">+ Nova obra</button>
      <button class="btn btn-claro" id="checar-sienge">🏗️ Checar novas obras (Sienge)</button>
      <button class="btn btn-claro" id="checar-email">🔍 Checar novas obras (e-mail)</button>
    </div>
    <div id="achadas"></div>
    <div id="lista"><p class="carregando">Carregando…</p></div>`;

  const lista = raiz.querySelector<HTMLElement>('#lista')!;
  const campoBusca = raiz.querySelector<HTMLInputElement>('#busca')!;
  let todas: ObraLista[] = [];

  // Busca no navegador, não no servidor: são 20 obras, e nunca serão milhares.
  // Ir ao servidor a cada tecla aqui seria custo sem ganho nenhum.
  campoBusca.addEventListener('input', () => desenhar());
  raiz.querySelector('#nova')!.addEventListener('click', () => abrirFicha(null));

  /**
   * 🔍 CHECAR NOVAS OBRAS — o botão do sistema antigo, de volta (11/08).
   * Procura os e-mails "OBRA FECHADA" na caixa do gestor e mostra o que ainda
   * não está cadastrado, com o cadastro a um clique. Nada é marcado no Gmail:
   * "já vista" = já existe obra com aquele código.
   */
  const achadas = raiz.querySelector<HTMLElement>('#achadas')!;

  /**
   * 🏗️ CHECAR NO SIENGE (21/08): a fonte direta — mostra os empreendimentos
   * mais recentes que ainda não estão aqui, com o ENDEREÇO já vindo junto
   * (o e-mail não traz). Cadastro a um clique. Só leitura no Sienge.
   */
  raiz.querySelector('#checar-sienge')!.addEventListener('click', () => {
    const b = raiz.querySelector<HTMLButtonElement>('#checar-sienge')!;
    b.disabled = true;
    b.textContent = '⏳ Consultando o Sienge…';
    void api
      .post<{
        encontradas: { numero: string; nome: string; endereco: string | null; criadaEm: string | null }[];
        totalSienge: number;
      }>('/api/obras/checar-sienge', {})
      .then((r) => {
        achadas.innerHTML = `
          <section class="ped-painel">
            <h3>🏗️ Obras novas no Sienge
              <span class="sub-inline">${r.encontradas.length} ainda não cadastrada(s) · ${r.totalSienge.toLocaleString('pt-BR')} no Sienge ao todo</span>
            </h3>
            ${
              r.encontradas.length === 0
                ? `<p class="dica">Tudo em dia — nenhuma obra nova no Sienge.</p>`
                : `<p class="dica">As 30 mais recentes de lá que não existem aqui. O endereço entra junto no cadastro.</p>` +
                  r.encontradas
                    .map(
                      (o) => `
              <div class="obra-achada">
                <span><b>${escapar(o.numero)}</b> — ${escapar(o.nome)}${
                  o.criadaEm ? ` <i class="sub-inline">criada ${o.criadaEm.slice(8, 10)}/${o.criadaEm.slice(5, 7)}/${o.criadaEm.slice(0, 4)}</i>` : ''
                }${o.endereco ? `<br><span class="sub-inline">📍 ${escapar(o.endereco)}</span>` : ''}</span>
                <button class="btn" data-sienge-num="${escapar(o.numero)}" data-sienge-nome="${escapar(o.nome)}"
                        data-sienge-end="${escapar(o.endereco ?? '')}">＋ Cadastrar</button>
              </div>`,
                    )
                    .join('')
            }
            <div class="conf-acoes"><button class="btn btn-claro" id="achadas-fechar">Fechar</button></div>
          </section>`;
        achadas.querySelector('#achadas-fechar')!.addEventListener('click', () => {
          achadas.innerHTML = '';
        });
        achadas.querySelectorAll<HTMLButtonElement>('[data-sienge-num]').forEach((bt) => {
          bt.addEventListener('click', () => {
            bt.disabled = true;
            bt.textContent = '⏳';
            // A importação busca o DETALHE no Sienge e preenche a ficha
            // inteira que houver lá (endereço, área, datas, responsável…).
            void api
              .post<{ preenchidos: string[] }>('/api/obras/importar-sienge', {
                id: Number(bt.dataset['siengeNum']),
              })
              .then((r) => {
                bt.replaceWith(Object.assign(document.createElement('span'), { textContent: '✓ cadastrada' }));
                avisar(
                  r.preenchidos.length
                    ? `Obra ${bt.dataset['siengeNum']} cadastrada com ${r.preenchidos.join(', ')} do Sienge.`
                    : `Obra ${bt.dataset['siengeNum']} cadastrada — o Sienge não tinha mais dados para preencher.`,
                );
                void carregar();
              })
              .catch((e) => {
                bt.disabled = false;
                bt.textContent = '＋ Cadastrar';
                avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui cadastrar.');
              });
          });
        });
      })
      .catch((e) => {
        achadas.innerHTML = `<p class="aviso">${escapar(
          e instanceof ErroApi ? e.message : 'Não consegui consultar o Sienge agora.',
        )}</p>`;
      })
      .finally(() => {
        b.disabled = false;
        b.textContent = '🏗️ Checar novas obras (Sienge)';
      });
  });

  raiz.querySelector('#checar-email')!.addEventListener('click', () => {
    const b = raiz.querySelector<HTMLButtonElement>('#checar-email')!;
    b.disabled = true;
    b.textContent = '⏳ Lendo o e-mail…';
    void api
      .post<{ encontradas: { numero: string; nome: string; jaCadastrada: boolean }[] }>(
        '/api/obras/checar-email',
        {},
      )
      .then((r) => {
        const novas = r.encontradas.filter((o) => !o.jaCadastrada);
        achadas.innerHTML = `
          <section class="ped-painel">
            <h3>📧 Obras nos e-mails "OBRA FECHADA"
              <span class="sub-inline">${novas.length} nova(s) · ${r.encontradas.length - novas.length} já cadastrada(s)</span>
            </h3>
            ${
              r.encontradas.length === 0
                ? `<p class="dica">Nenhum e-mail "OBRA FECHADA" encontrado.</p>`
                : r.encontradas
                    .map(
                      (o) => `
              <div class="obra-achada${o.jaCadastrada ? ' ja-tem' : ''}">
                <span><b>${escapar(o.numero)}</b> — ${escapar(o.nome)}</span>
                ${
                  o.jaCadastrada
                    ? `<span class="sub-inline">✓ já cadastrada</span>`
                    : `<button class="btn" data-cad-obra="${escapar(o.numero)}" data-cad-nome="${escapar(o.nome)}">＋ Cadastrar</button>`
                }
              </div>`,
                    )
                    .join('')
            }
            <div class="conf-acoes"><button class="btn btn-claro" id="achadas-fechar">Fechar</button></div>
          </section>`;
        achadas.querySelector('#achadas-fechar')!.addEventListener('click', () => {
          achadas.innerHTML = '';
        });
        achadas.querySelectorAll<HTMLButtonElement>('[data-cad-obra]').forEach((bt) => {
          bt.addEventListener('click', () => {
            bt.disabled = true;
            bt.textContent = '⏳';
            // O código da obra É o número do Sienge — é assim que as reais estão.
            void api
              .put(`/api/obras/${encodeURIComponent(bt.dataset['cadObra']!)}`, {
                nome: bt.dataset['cadNome'],
                codigoSienge: bt.dataset['cadObra'],
              })
              .then(() => {
                bt.replaceWith(Object.assign(document.createElement('span'), { textContent: '✓ cadastrada' }));
                avisar(
                  `Obra ${bt.dataset['cadObra']} cadastrada — abra a ficha para completar o ENDEREÇO (o motorista depende dele).`,
                );
                void carregar();
              })
              .catch((e) => {
                bt.disabled = false;
                bt.textContent = '＋ Cadastrar';
                avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui cadastrar.');
              });
          });
        });
      })
      .catch((e) => {
        achadas.innerHTML = `<p class="aviso">${escapar(
          e instanceof ErroApi ? e.message : 'Não consegui ler o e-mail agora.',
        )}</p>`;
      })
      .finally(() => {
        b.disabled = false;
        b.textContent = '🔍 Checar novas obras (e-mail)';
      });
  });

  // A ORDEM vai ao servidor, e não é feita aqui, porque "por código" com
  // acento e caixa mistos ordena diferente em cada navegador — o banco tem uma
  // regra só, e é a mesma que a listagem de qualquer outra tela usa.
  raiz.querySelector<HTMLSelectElement>('#ordem')!.addEventListener('change', (e) => {
    ordem = (e.target as HTMLSelectElement).value as typeof ordem;
    void carregar();
  });

  raiz.querySelector('#abas-obra')!.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-sit]');
    if (!b) return;
    situacao = b.dataset['sit'] as typeof situacao;
    raiz.querySelectorAll<HTMLElement>('[data-sit]').forEach((x) => {
      x.setAttribute('aria-pressed', String(x.dataset['sit'] === situacao));
    });
    void carregar();
  });

  async function carregar() {
    try {
      const r = await api.get<{ obras: ObraLista[] }>(
        `/api/obras?situacao=${situacao}&ordem=${ordem}`,
      );
      todas = r.obras;
      desenhar();
    } catch (erro) {
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof Error ? erro.message : 'Não consegui carregar.',
      )}</p>`;
    }
  }

  function desenhar() {
    const termo = campoBusca.value.trim().toLowerCase();
    const obras = termo
      ? todas.filter((o) =>
          [o.nome, o.codigo, o.cliente, o.engenheiro, o.mestre]
            .filter(Boolean)
            .some((v) => v!.toLowerCase().includes(termo)),
        )
      : todas;

    if (obras.length === 0) {
      lista.innerHTML = `<div class="vazio"><p>${
        situacao === 'concluidas'
          ? 'Nenhuma obra concluída ainda.'
          : `Nenhuma obra${termo ? ' para essa busca' : ''}.`
      }</p></div>`;
      return;
    }

    lista.innerHTML = `
      <p class="dica">${obras.length} obra(s)${
        situacao === 'concluidas'
          ? ' concluída(s). Elas não aparecem em nenhum outro módulo — o histórico delas continua inteiro.'
          : '.'
      }</p>
      <div class="rolagem">
        <table class="tabela">
          <thead><tr>
            <th>Código</th><th>Obra</th><th>Endereço</th><th>Engenheiro</th><th>Mestre</th><th></th>
          </tr></thead>
          <tbody>${obras
            .map(
              (o) => `<tr>
                <td class="cod">${escapar(o.codigo)}</td>
                <td>${escapar(o.nome)}${
                  o.cliente ? `<span class="sub">${escapar(o.cliente)}</span>` : ''
                }</td>
                <td>${
                  o.endereco
                    ? escapar(o.endereco)
                    : '<span class="falta">sem endereço — o motorista não verá o mapa</span>'
                }</td>
                <td>${escapar(o.engenheiro ?? '—')}</td>
                <td>${escapar(o.mestre ?? '—')}</td>
                <td><button class="btn btn-claro" data-abrir="${escapar(o.codigo)}">Abrir</button></td>
              </tr>`,
            )
            .join('')}</tbody>
        </table>
      </div>`;

    lista.querySelectorAll<HTMLElement>('[data-abrir]').forEach((b) => {
      b.addEventListener('click', () => {
        abrirFicha(todas.find((o) => o.codigo === b.dataset['abrir']) ?? null);
      });
    });
  }

  /**
   * A listagem não traz todos os campos, então abrir uma obra existente busca a
   * ficha completa. Sem isto, salvar apagaria o que não estava na tela — o
   * mesmo tipo de perda silenciosa que a galeria de fotos evita mandando tudo.
   */
  function abrirFicha(obra: ObraLista | null) {
    if (obra) {
      lista.innerHTML = `<p class="carregando">Abrindo ${escapar(obra.codigo)}…</p>`;
      void (async () => {
        try {
          desenharFicha(
            await api.get<Record<string, unknown>>(`/api/obras/${encodeURIComponent(obra.codigo)}`),
          );
        } catch (erro) {
          lista.innerHTML = `<p class="aviso">${escapar(
            erro instanceof ErroApi ? erro.message : 'Não consegui abrir a obra.',
          )}</p>`;
        }
      })();
      return;
    }
    desenharFicha(null);
  }

  function desenharFicha(obra: Record<string, unknown> | null) {
    const valor = (campo: string) => {
      // O banco usa nome_com_underscore; a API recebe nomeEmCamelo.
      const emBanco = campo.replace(/[A-Z]/g, (c) => '_' + c.toLowerCase());
      const v = obra?.[campo] ?? obra?.[emBanco];
      return v == null ? '' : String(v);
    };
    const novo = !obra;

    lista.innerHTML = `
      <button class="voltar" id="voltar">← Todas as obras</button>
      <form class="ficha" id="ficha" novalidate>
        <header class="ficha-topo">
          <div>
            <span class="rotulo">${novo ? 'Nova obra' : 'Obra'}</span>
            <h2>${escapar(valor('nome') || 'Cadastrar')}</h2>
          </div>
          ${valor('concluida_em') ? `<span class="estado e-entregue">Concluída</span>` : ''}
        </header>

        <div class="form-grade">
          ${CAMPOS.map(
            (c) => `
            <label class="rotulado ${c.largo ? 'largo' : ''}">
              <span>${escapar(c.rotulo)}</span>
              <input name="${c.nome}" type="${c.tipo ?? 'text'}"
                     value="${escapar(valor(c.nome))}"
                     ${c.nome === 'codigo' && !novo ? 'readonly' : ''}>
              ${c.dica ? `<small class="dica">${escapar(c.dica)}</small>` : ''}
            </label>`,
          ).join('')}
        </div>

        <h3>Observações</h3>
        <label class="rotulado"><textarea name="obs" rows="3" maxlength="2000">${escapar(valor('obs'))}</textarea></label>

        <div class="conf-acoes">
          <button type="submit" class="btn" id="salvar">${novo ? 'Cadastrar' : 'Salvar'}</button>
          ${novo ? '' : `<button type="button" class="btn btn-claro" id="buscar-sienge">🏗️ Buscar no Sienge</button>`}
          ${
            novo
              ? ''
              : valor('concluida_em')
                ? `<button type="button" class="btn btn-claro" id="conclusao" data-abrir-de-novo>Reabrir obra</button>`
                : `<button type="button" class="btn btn-claro" id="conclusao">✓ Marcar como concluída</button>`
          }
        </div>
        <p class="aviso" id="erro" hidden></p>
      </form>`;

    lista.querySelector('#voltar')!.addEventListener('click', () => void carregar());
    const form = lista.querySelector<HTMLFormElement>('#ficha')!;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void salvar(form, novo);
    });
    form.querySelector('#conclusao')?.addEventListener('click', () => {
      void concluir(valor('codigo'), valor('nome'), !valor('concluida_em'));
    });

    /**
     * 🏗️ BUSCAR NO SIENGE (02/09, Lucas): mesmo numa obra criada à mão, o
     * botão traz o que apareceu de novo lá — usa o "Código no Sienge" da
     * ficha (vale o que está digitado, mesmo sem salvar). Campo vazio no
     * Sienge nunca apaga o que foi preenchido aqui.
     */
    form.querySelector('#buscar-sienge')?.addEventListener('click', () => {
      const b = form.querySelector<HTMLButtonElement>('#buscar-sienge')!;
      const digitado = form.querySelector<HTMLInputElement>('[name="codigoSienge"]')!.value.trim();
      b.disabled = true;
      b.textContent = '⏳ Consultando…';
      void api
        .post<{ nomeSienge: string; preenchidos: string[] }>(
          `/api/obras/${encodeURIComponent(valor('codigo'))}/completar-sienge`,
          digitado && /^\d+$/.test(digitado) ? { siengeId: Number(digitado) } : {},
        )
        .then((r) => {
          avisar(
            r.preenchidos.length
              ? `Ficha completada do Sienge (${r.nomeSienge}): ${r.preenchidos.join(', ')}.`
              : `O Sienge não tinha nada além do que já está aqui.`,
          );
          // Reabre a ficha para os campos novos aparecerem preenchidos.
          void abrirFicha({ codigo: valor('codigo') } as ObraLista);
        })
        .catch((e) => {
          b.disabled = false;
          b.textContent = '🏗️ Buscar no Sienge';
          avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui consultar o Sienge.');
        });
    });
  }

  async function concluir(codigo: string, nome: string, concluir: boolean): Promise<void> {
    const erro = lista.querySelector<HTMLElement>('#erro')!;
    if (
      concluir &&
      !confirm(
        `Marcar "${nome}" como concluída?\n\n` +
          `Ela sai da vitrine de pedidos e da criação de romaneios — ninguém mais consegue pedir ` +
          `material para ela. O histórico (romaneios, pedidos, movimentos) continua inteiro, e dá para reabrir.`,
      )
    ) {
      return;
    }
    try {
      await api.patch(`/api/obras/${encodeURIComponent(codigo)}/conclusao`, { concluida: concluir });
      avisar(concluir ? `Obra "${nome}" concluída` : `Obra "${nome}" reaberta`);
      await carregar();
    } catch (e) {
      // O 409 (romaneio em aberto) vem com a sugestão do que fazer antes.
      erro.textContent =
        e instanceof ErroApi
          ? `${e.message}${e.sugestao ? ' ' + e.sugestao : ''}`
          : 'Não consegui alterar a situação da obra.';
      erro.hidden = false;
      erro.scrollIntoView({ block: 'nearest' });
    }
  }

  async function salvar(form: HTMLFormElement, novo: boolean) {
    const erro = form.querySelector<HTMLElement>('#erro')!;
    const d = new FormData(form);
    const texto = (n: string) => String(d.get(n) ?? '').trim();

    if (!texto('codigo') || !texto('nome')) {
      erro.textContent = 'Código e nome são obrigatórios.';
      erro.hidden = false;
      return;
    }

    const corpo: Record<string, unknown> = { obs: texto('obs') || null };
    for (const c of CAMPOS) {
      if (c.nome === 'codigo') continue;
      const v = texto(c.nome);
      corpo[c.nome] = v === '' ? null : c.tipo === 'number' ? Number(v) : v;
    }

    const botao = form.querySelector<HTMLButtonElement>('#salvar')!;
    botao.disabled = true;
    botao.textContent = 'Salvando…';
    try {
      const r = await api.put<{ aviso?: string }>(
        `/api/obras/${encodeURIComponent(texto('codigo'))}`,
        corpo,
      );
      await carregar();
      // Salvou, mas com uma falta que só dói depois — mostrar é o ponto.
      avisar(novo ? `Obra ${texto('codigo')} cadastrada` : `Obra ${texto('codigo')} salva`);
      if (r.aviso) {
        const topo = lista.querySelector('.dica');
        topo?.insertAdjacentHTML('beforebegin', `<p class="aviso">${escapar(r.aviso)}</p>`);
      }
    } catch (e) {
      botao.disabled = false;
      botao.textContent = novo ? 'Cadastrar' : 'Salvar';
      erro.textContent =
        e instanceof ErroApi
          ? e.message + (e.detalhes?.length ? ` (${e.detalhes.join('; ')})` : '')
          : 'Não consegui salvar.';
      erro.hidden = false;
    }
  }

  void carregar();
}
